import React, { useRef, useState } from "react";
import {
    Autocomplete,
    Chip,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField,
} from "@mui/material";
import axios from "axios";

export type AirlineMode = "" | "preferredAirlines" | "prohibitedAirlines";

export type PreferredAirline = {
    airline: string;
    days: string[];
};

export const DAY_OPTIONS = [
    { label: "M", value: "Mon" },
    { label: "T", value: "Tue" },
    { label: "W", value: "Wed" },
    { label: "T", value: "Thu" },
    { label: "F", value: "Fri" },
    { label: "S", value: "Sat" },
    { label: "S", value: "Sun" },
] as const;

export const emptyPreferredRow = (): PreferredAirline => ({ airline: "", days: [] });

export type CarrierOption = {
    code: string;
    label: string;
};

const toCarrierCode = (value: string | CarrierOption | null): string => {
    if (!value) return "";
    if (typeof value === "string") return value.trim().toUpperCase();
    return String(value.code || "").trim().toUpperCase();
};

export const searchCarriers = async (query: string): Promise<CarrierOption[]> => {
    const value = String(query || "").trim();
    if (!value) return [];
    try {
        const url = `${process.env.REACT_APP_STATIC_CONTENT}/carrier/search/${encodeURIComponent(value)}`;
        const result = await axios(url);
        if (!result?.data) return [];

        let hits = result.data.filter((item: any) => item._score === "1");
        if (!hits.length) {
            hits = result.data;
        }

        return hits
            .map((item: any) => {
                const code = item?._source?.carrierCode;
                if (!code) return null;
                const name = item?._source?.carrierName || "";
                return {
                    code: String(code).toUpperCase(),
                    label: name ? `${code}-${name}` : String(code).toUpperCase(),
                } as CarrierOption;
            })
            .filter(Boolean) as CarrierOption[];
    } catch (error) {
        console.log("carrier search failed", error);
        return [];
    }
};

export const buildAirlinePayload = (
    airlineMode: AirlineMode,
    preferredAirlines: PreferredAirline[],
    prohibitedAirlines: string[]
) => {
    if (airlineMode === "preferredAirlines") {
        return {
            preferredAirlines: (Array.isArray(preferredAirlines) ? preferredAirlines : [])
                .filter((row) => row?.airline)
                .map((row) => ({
                    airline: String(row.airline).toUpperCase(),
                    days: Array.isArray(row.days) ? row.days : [],
                })),
            prohibitedAirlines: [] as string[],
        };
    }
    if (airlineMode === "prohibitedAirlines") {
        return {
            preferredAirlines: [] as PreferredAirline[],
            prohibitedAirlines: (Array.isArray(prohibitedAirlines) ? prohibitedAirlines : [])
                .map((code) => String(code).toUpperCase())
                .filter(Boolean),
        };
    }
    return {
        preferredAirlines: [] as PreferredAirline[],
        prohibitedAirlines: [] as string[],
    };
};

export const hydrateAirlineMode = (search: any): AirlineMode => {
    if (Array.isArray(search?.preferredAirlines) && search.preferredAirlines.length) {
        return "preferredAirlines";
    }
    if (Array.isArray(search?.prohibitedAirlines) && search.prohibitedAirlines.length) {
        return "prohibitedAirlines";
    }
    return "";
};

export const hydratePreferredAirlines = (search: any): PreferredAirline[] => {
    if (!Array.isArray(search?.preferredAirlines) || !search.preferredAirlines.length) {
        return [emptyPreferredRow()];
    }
    return search.preferredAirlines.map((row: any) => ({
        airline: String(row?.airline || "").toUpperCase(),
        days: Array.isArray(row?.days) ? row.days : [],
    }));
};

export const hydrateProhibitedAirlines = (search: any): string[] => {
    if (!Array.isArray(search?.prohibitedAirlines)) return [];
    return search.prohibitedAirlines.map((code: any) => String(code).toUpperCase()).filter(Boolean);
};

export const formatAirlineRestrictionDisplay = (search: any): React.ReactNode => {
    if (Array.isArray(search?.preferredAirlines) && search.preferredAirlines.length) {
        return search.preferredAirlines.map((row: PreferredAirline, index: number) => (
            <div key={`${row.airline}-${index}`}>
                {row.airline}
                {Array.isArray(row.days) && row.days.length ? ` (${row.days.join(",")})` : ""}
            </div>
        ));
    }
    if (Array.isArray(search?.prohibitedAirlines) && search.prohibitedAirlines.length) {
        return search.prohibitedAirlines.map((code: string) => <div key={code}>{code}</div>);
    }
    return "-";
};

type AirlineRestrictionFieldsProps = {
    airlineMode: AirlineMode;
    preferredAirlines: PreferredAirline[];
    prohibitedAirlines: string[];
    onModeChange: (mode: AirlineMode) => void;
    onPreferredChange: (rows: PreferredAirline[]) => void;
    onProhibitedChange: (codes: string[]) => void;
};

export const AirlineRestrictionFields = ({
    airlineMode,
    preferredAirlines,
    prohibitedAirlines,
    onModeChange,
    onPreferredChange,
    onProhibitedChange,
}: AirlineRestrictionFieldsProps) => {
    const [carrierOptions, setCarrierOptions] = useState<CarrierOption[]>([]);
    const lastQueryRef = useRef("");
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const requestIdRef = useRef(0);

    const handleCarrierInput = (rawInput: string) => {
        const value = String(rawInput || "").trim().toUpperCase();

        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
            searchTimerRef.current = null;
        }

        if (!value) {
            lastQueryRef.current = "";
            setCarrierOptions((prev) => (prev.length ? [] : prev));
            return;
        }

        if (value === lastQueryRef.current) {
            return;
        }

        searchTimerRef.current = setTimeout(async () => {
            if (value === lastQueryRef.current) {
                return;
            }
            const requestId = ++requestIdRef.current;
            lastQueryRef.current = value;
            const options = await searchCarriers(value);
            if (requestId !== requestIdRef.current) {
                return;
            }
            setCarrierOptions(options);
        }, 300);
    };

    const handleModeChange = (mode: AirlineMode) => {
        if (mode === airlineMode) {
            return;
        }
        onModeChange(mode);
        setCarrierOptions([]);
        lastQueryRef.current = "";
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
            searchTimerRef.current = null;
        }

        if (mode === "preferredAirlines") {
            onProhibitedChange([]);
            if (!preferredAirlines?.length) {
                onPreferredChange([emptyPreferredRow()]);
            }
        } else if (mode === "prohibitedAirlines") {
            onPreferredChange([emptyPreferredRow()]);
        }
    };

    const updatePreferredRow = (index: number, patch: Partial<PreferredAirline>) => {
        const current = preferredAirlines[index];
        if (!current) return;

        const nextAirline = patch.airline !== undefined ? patch.airline : current.airline;
        const nextDays = patch.days !== undefined ? patch.days : current.days;
        if (
            nextAirline === current.airline &&
            JSON.stringify(nextDays) === JSON.stringify(current.days)
        ) {
            return;
        }

        onPreferredChange(
            preferredAirlines.map((row, i) => (i === index ? { ...row, ...patch } : row))
        );
    };

    const toggleDay = (index: number, day: string) => {
        const row = preferredAirlines[index];
        const days = Array.isArray(row?.days) ? [...row.days] : [];
        const nextDays = days.includes(day)
            ? days.filter((d) => d !== day)
            : [...days, day];
        updatePreferredRow(index, { days: nextDays });
    };

    const addPreferredRow = () => {
        onPreferredChange([...(preferredAirlines || []), emptyPreferredRow()]);
    };

    const removePreferredRow = (index: number) => {
        if ((preferredAirlines || []).length <= 1) {
            onPreferredChange([emptyPreferredRow()]);
            return;
        }
        onPreferredChange(preferredAirlines.filter((_, i) => i !== index));
    };

    return (
        <div className="airlineRestrictionFields">
            <RadioGroup
                aria-label="airlineMode"
                className="sun__radioBtn"
                name="airlineMode"
                value={airlineMode || ""}
                onChange={(e) => handleModeChange(e.target.value as AirlineMode)}
            >
                <FormControlLabel
                    value="preferredAirlines"
                    control={<Radio color="primary" size="small" />}
                    label="Preferred Airlines"
                />
                <FormControlLabel
                    value="prohibitedAirlines"
                    control={<Radio color="primary" size="small" />}
                    label="Prohibited Airlines"
                />
            </RadioGroup>

            {airlineMode === "preferredAirlines" && (
                <div className="preferredAirlinesBlock">
                    {(preferredAirlines || [emptyPreferredRow()]).map((row, index) => (
                        <div className="preferredAirlineRow" key={`preferred-${index}`}>
                            <Autocomplete
                                freeSolo
                                size="small"
                                options={carrierOptions}
                                filterOptions={(options) => options}
                                getOptionLabel={(option) =>
                                    typeof option === "string" ? option : option.label
                                }
                                isOptionEqualToValue={(option, value) =>
                                    toCarrierCode(option as string | CarrierOption) ===
                                    toCarrierCode(value as string | CarrierOption)
                                }
                                value={row.airline || null}
                                onChange={(_, value) => {
                                    updatePreferredRow(index, {
                                        airline: toCarrierCode(value as string | CarrierOption),
                                    });
                                }}
                                onInputChange={(_, inputValue, reason) => {
                                    // Only react to real typing — never to reset/clear feedback loops
                                    if (reason === "input") {
                                        handleCarrierInput(inputValue);
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        placeholder="Airline"
                                        className="form-control airlineAutocomplete"
                                        onBlur={(e) => {
                                            params.inputProps.onBlur?.(e as any);
                                            const typed = String(e.target.value || "")
                                                .trim()
                                                .toUpperCase();
                                            if (typed !== (row.airline || "")) {
                                                updatePreferredRow(index, { airline: typed });
                                            }
                                        }}
                                    />
                                )}
                            />
                            <div className="dayStrip">
                                {DAY_OPTIONS.map((day) => (
                                    <button
                                        type="button"
                                        key={`${index}-${day.value}`}
                                        className={`dayStripBtn${row.days?.includes(day.value) ? " active" : ""}`}
                                        onClick={() => toggleDay(index, day.value)}
                                    >
                                        {day.label}
                                    </button>
                                ))}
                            </div>
                            <div className="preferredAirlineActions">
                                <button
                                    type="button"
                                    className="airlineRowBtn"
                                    onClick={addPreferredRow}
                                    title="Add airline"
                                >
                                    +
                                </button>
                                <button
                                    type="button"
                                    className="airlineRowBtn"
                                    onClick={() => removePreferredRow(index)}
                                    title="Remove airline"
                                >
                                    −
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {airlineMode === "prohibitedAirlines" && (
                <Autocomplete
                    multiple
                    freeSolo
                    size="small"
                    options={carrierOptions}
                    filterOptions={(options) => options}
                    getOptionLabel={(option) =>
                        typeof option === "string" ? option : option.label
                    }
                    isOptionEqualToValue={(option, value) =>
                        toCarrierCode(option as string | CarrierOption) ===
                        toCarrierCode(value as string | CarrierOption)
                    }
                    value={prohibitedAirlines}
                    onChange={(_, value) => {
                        const codes = value
                            .map((item) => toCarrierCode(item as string | CarrierOption))
                            .filter(Boolean);
                        const unique = Array.from(new Set(codes));
                        if (
                            unique.length === prohibitedAirlines.length &&
                            unique.every((code, i) => code === prohibitedAirlines[i])
                        ) {
                            return;
                        }
                        onProhibitedChange(unique);
                    }}
                    onInputChange={(_, inputValue, reason) => {
                        if (reason === "input") {
                            handleCarrierInput(inputValue);
                        }
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                size="small"
                                label={String(option)}
                                {...getTagProps({ index })}
                            />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Select airlines"
                            className="form-control airlineAutocomplete"
                        />
                    )}
                />
            )}
        </div>
    );
};
